import { ChangeEvent, FormEvent } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";

import { TransformationReferenceAdmin } from "src/api/sure-api/useTransformationReferencesAdmin";
import { TransformationReferenceFormState } from "./TransformationReferenceDialogs";

type TransformationReferencesLegacyViewProps = {
    title: string;
    subtitle: string;
    search: string;
    references: TransformationReferenceAdmin[];
    isLoading: boolean;
    isError: boolean;
    isDialogOpen: boolean;
    editingReference: TransformationReferenceAdmin | null;
    form: TransformationReferenceFormState;
    isSaving: boolean;
    onSearchChange: (search: string) => void;
    onCreate: () => void;
    onEdit: (reference: TransformationReferenceAdmin) => void;
    onDelete: (reference: TransformationReferenceAdmin) => void;
    onRetry: () => void;
    onCloseDialog: () => void;
    onFormChange: (form: TransformationReferenceFormState) => void;
    onSubmit: () => void;
};

export const TransformationReferencesLegacyView = (props: TransformationReferencesLegacyViewProps) => {
    const {
        title,
        subtitle,
        search,
        references,
        isLoading,
        isError,
        isDialogOpen,
        editingReference,
        form,
        isSaving,
        onSearchChange,
        onCreate,
        onEdit,
        onDelete,
        onRetry,
        onCloseDialog,
        onFormChange,
        onSubmit,
    } = props;
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <>
            <Box sx={{ padding: theme.spacing(4) }}>
                <Paper sx={{ borderRadius: theme.shape.borderRadius * 3, overflow: "hidden" }}>
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "stretch", md: "center" }}
                        gap={theme.spacing(2)}
                        sx={{
                            borderBottom: `${theme.custom.strokes.containerDivider.width} ${theme.custom.strokes.containerDivider.style} ${theme.custom.strokes.containerDivider.color}`,
                            padding: theme.spacing(3),
                        }}
                    >
                        <Box>
                            <Typography variant="h1">{title}</Typography>
                            <Typography variant="body-regular" color="text.secondary">
                                {subtitle}
                            </Typography>
                        </Box>
                        <Button variant="contained" onClick={onCreate}>
                            {t("transformation.references.add", "Add process")}
                        </Button>
                    </Stack>

                    <Stack gap={theme.spacing(2)} sx={{ padding: theme.spacing(3) }}>
                        <TextField
                            placeholder={t("transformation.references.search", "Search processes...")}
                            value={search}
                            onChange={(event) => onSearchChange(event.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ maxWidth: theme.spacing(52.5) }}
                        />

                        {isLoading ?
                            <Box sx={{ display: "flex", justifyContent: "center", padding: theme.spacing(4) }}>
                                <CircularProgress />
                            </Box>
                        : isError ?
                            <Alert
                                severity="error"
                                action={
                                    <Button color="inherit" size="small" onClick={onRetry}>
                                        {t("common.actions.refresh", "Refresh")}
                                    </Button>
                                }
                            >
                                {t("transformation.references.load_error", "Unable to load transformation processes.")}
                            </Alert>
                        :   <TableContainer>
                                <Table aria-label={title}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t("common.code", "Code")}</TableCell>
                                            <TableCell>{t("common.name", "Name")}</TableCell>
                                            <TableCell>
                                                {t(
                                                    "transformation.references.ecobalyse_uuid",
                                                    "Ecobalyse process UUID",
                                                )}
                                            </TableCell>
                                            <TableCell>{t("common.scores", "Scores")}</TableCell>
                                            <TableCell align="right">{t("common.actions", "Actions")}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {references.map((reference) => (
                                            <TableRow key={reference.id}>
                                                <TableCell>{reference.code}</TableCell>
                                                <TableCell>{reference.name}</TableCell>
                                                <TableCell>{reference.ecobalyseProcessUuid ?? "–"}</TableCell>
                                                <TableCell>{reference.scores?.length ?? 0}</TableCell>
                                                <TableCell align="right">
                                                    <IconButton
                                                        aria-label={`${t("common.actions.edit", "Modify")} ${reference.name}`}
                                                        onClick={() => onEdit(reference)}
                                                    >
                                                        <Edit />
                                                    </IconButton>
                                                    <IconButton
                                                        aria-label={`${t("common.actions.delete", "Delete")} ${reference.name}`}
                                                        onClick={() => onDelete(reference)}
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {references.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={5}>
                                                    <Typography variant="body-regular">
                                                        {t(
                                                            "transformation.references.empty",
                                                            "No transformation process found.",
                                                        )}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        }
                    </Stack>
                </Paper>
            </Box>

            <TransformationReferenceLegacyDialog
                open={isDialogOpen}
                form={form}
                isSaving={isSaving}
                isEditing={editingReference !== null}
                onClose={onCloseDialog}
                onChange={onFormChange}
                onSubmit={onSubmit}
            />
        </>
    );
};

type TransformationReferenceLegacyDialogProps = {
    open: boolean;
    form: TransformationReferenceFormState;
    isSaving: boolean;
    isEditing: boolean;
    onClose: () => void;
    onChange: (form: TransformationReferenceFormState) => void;
    onSubmit: () => void;
};

const TransformationReferenceLegacyDialog = (props: TransformationReferenceLegacyDialogProps) => {
    const { open, form, isSaving, isEditing, onClose, onChange, onSubmit } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const title =
        isEditing ?
            t("transformation.references.edit_title", "Edit transformation process")
        :   t("transformation.references.create_title", "Create transformation process");
    const updateField = (field: keyof TransformationReferenceFormState) => (event: ChangeEvent<HTMLInputElement>) => {
        onChange({ ...form, [field]: event.target.value });
    };

    return (
        <Dialog
            open={open}
            onClose={isSaving ? undefined : onClose}
            fullWidth
            maxWidth="sm"
            aria-labelledby="legacy-dialog-title"
        >
            <Box
                component="form"
                onSubmit={(event: FormEvent<HTMLFormElement>) => {
                    event.preventDefault();
                    onSubmit();
                }}
            >
                <DialogTitle id="legacy-dialog-title">{title}</DialogTitle>
                <DialogContent>
                    <Stack gap={theme.spacing(2)} sx={{ paddingTop: theme.spacing(1) }}>
                        <TextField
                            label={t("common.code", "Code")}
                            value={form.code}
                            onChange={updateField("code")}
                            required
                            disabled={isSaving}
                            fullWidth
                        />
                        <TextField
                            label={t("common.name", "Name")}
                            value={form.name}
                            onChange={updateField("name")}
                            required
                            disabled={isSaving}
                            fullWidth
                        />
                        <TextField
                            label={t("transformation.references.ecobalyse_uuid", "Ecobalyse process UUID")}
                            value={form.ecobalyseProcessUuid}
                            onChange={updateField("ecobalyseProcessUuid")}
                            disabled={isSaving}
                            fullWidth
                        />
                        <TextField
                            label={t("transformation.references.ssq_iri", "SSQ IRI")}
                            value={form.ssq}
                            onChange={updateField("ssq")}
                            placeholder="/api/ssqs/1"
                            disabled={isSaving}
                            fullWidth
                        />
                        <TextField
                            label={t("transformation.references.source_version_iri", "Source version IRI")}
                            value={form.sourceVersion}
                            onChange={updateField("sourceVersion")}
                            placeholder="/api/source_versions/1"
                            disabled={isSaving}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={isSaving}>
                        {t("common.cancel", "Cancel")}
                    </Button>
                    <Button type="submit" variant="contained" disabled={isSaving}>
                        {isSaving ?
                            <CircularProgress size={theme.spacing(2.5)} />
                        :   t("common.save", "Save")}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};
